import { useState, useEffect, useRef } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { usePrefs } from '../contexts/PrefsContext';

function AddCustomLink({ onAdded }) {
  const { addCustomLink } = usePrefs();
  // Form state
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [emoji, setEmoji] = useState('😊');
  const [backgroundColor, setBackgroundColor] = useState('#D1D5DB');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs for click outside detection
  const emojiPickerRef = useRef(null);
  const colorPickerRef = useRef(null);
  
  // Web-safe color palette
  const webSafeColors = [
    '#FF0000', '#FF3300', '#FF6600', '#FF9900', '#FFCC00', '#FFFF00',
    '#CCFF00', '#99FF00', '#66FF00', '#33FF00', '#00FF00', '#00FF33',
    '#00FF66', '#00FF99', '#00FFCC', '#00FFFF', '#00CCFF', '#0099FF',
    '#0066FF', '#0033FF', '#0000FF', '#3300FF', '#6600FF', '#9900FF',
    '#CC00FF', '#FF00FF', '#FF00CC', '#FF0099', '#FF0066', '#FF0033',
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#800000', '#808000', '#008000', '#008080', '#000080', '#800080',
    '#C0C0C0', '#808080', '#FF6666', '#66FF66', '#6666FF', '#FFFF66',
    '#FF66FF', '#66FFFF', '#990000', '#009900', '#000099', '#999900',
    '#990099', '#009999', '#CC0000', '#00CC00', '#0000CC', '#CCCC00',
    '#CC00CC', '#00CCCC'
  ];

  const handleAddCustomLink = async (e) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) return;

    const newLink = {
      name: name.trim(),
      url: url.trim(),
      emoji: emoji,
      backgroundColor: backgroundColor,
      id: Date.now(),
      isCustom: true
    };

    setSaving(true);
    const saved = await addCustomLink(newLink);
    setSaving(false);

    // A failed write simply didn't happen (D10). Leave the form filled in so
    // the user can try again rather than closing as if it had worked - the
    // error itself is shown by My Games.
    if (!saved) return;

    setName('');
    setUrl('');
    setEmoji('😊');
    setBackgroundColor('#D1D5DB');
    setShowEmojiPicker(false);
    setShowColorPicker(false);

    if (onAdded) onAdded();
  };

  const onEmojiClick = (emojiObject) => {
    setEmoji(emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <p className="text-blue-600 text-sm mb-3">Add a game that's not on our list, or a convenient link to any other website you visit regularly.</p>
      <p className="text-blue-600 text-sm mb-3">Provide the name and full URL, then pick an icon and background color.</p>
      <form onSubmit={handleAddCustomLink} className="space-y-2">
        <div className="flex items-center space-x-2">
          <div className="relative" ref={emojiPickerRef}>
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="w-10 h-10 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800  transition-colors duration-200 flex items-center justify-center text-2xl sm:text-3xl"
              style={{ backgroundColor: backgroundColor }}
            >
              {emoji}
            </button>
            
            {showEmojiPicker && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white border border-blue-300 rounded-lg shadow-lg">
                  <EmojiPicker
                    onEmojiClick={onEmojiClick}
                    width={300}
                    height={400}
                    previewConfig={{
                      showPreview: false
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
            placeholder="Enter link label"
            required
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="relative" ref={colorPickerRef}>
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="w-10 h-10 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800 bg-white  transition-colors duration-200 flex items-center justify-center"
            >
              <img
                src="/img/colorwheel.png"
                alt="Color wheel"
                className="w-6 h-6"
              />
            </button>
            
            {showColorPicker && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white border border-blue-300 rounded-lg shadow-lg p-4">
                  <div className="grid grid-cols-12 gap-1 max-w-md">
                    {webSafeColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          setBackgroundColor(color);
                          setShowColorPicker(false);
                        }}
                        className={`w-6 h-6 rounded border-2 transition-all duration-200 ${
                          backgroundColor === color ? 'border-blue-800 shadow-lg' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
            placeholder="https://www.mylink.com"
            required
          />
        </div>
        
        <button
          type="submit"
          disabled={saving}
          className="w-full bg-yellow-400 border-2 border-blue-800 text-blue-800 px-4 py-2 rounded-lg font-bold transition-colors duration-200 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Add Link'}
        </button>
      </form>
    </div>
  );
}

export default AddCustomLink;